import React, { useEffect } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import * as XLSX from 'xlsx';

import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import CloseIcon from '@mui/icons-material/Close';

import { fetchInventoryReport } from '../../../api';

const INVENTORY_REPORT_COLUMNS = [
  { key: 'Item', label: 'Item', align: 'left', type: 'text', width: 32 },
  { key: 'Brand', label: 'Brand', align: 'left', type: 'text', width: 24 },
  { key: 'Generic', label: 'Generic', align: 'left', type: 'text', width: 24 },
  { key: 'Batch', label: 'Batch', align: 'left', type: 'text', width: 16 },
  { key: 'Strength', label: 'Strength', align: 'left', type: 'text', width: 14 },
  { key: 'Supplier', label: 'Supplier', align: 'left', type: 'text', width: 20 },
  { key: 'Expiry Date', label: 'Expiry Date', align: 'left', type: 'text', width: 16 },
  { key: 'Expiry Status', label: 'Expiry Status', align: 'left', type: 'text', width: 18 },
  { key: 'Quantity', label: 'Quantity', align: 'right', type: 'number', width: 12 },
  { key: 'Wholesale Price', label: 'Wholesale Price', align: 'right', type: 'money', width: 18 },
  { key: 'Retail Price', label: 'Retail Price', align: 'right', type: 'money', width: 16 },
  { key: 'Discount', label: 'Discount %', align: 'right', type: 'percent', width: 14 },
  { key: 'Final Retail Price', label: 'Final Retail Price', align: 'right', type: 'money', width: 20 },
  { key: 'Stock Value', label: 'Stock Value', align: 'right', type: 'money', width: 16 },
  { key: 'Expected Sales Value', label: 'Expected Sales Value', align: 'right', type: 'money', width: 22 },
  { key: 'Expected Profit', label: 'Expected Profit', align: 'right', type: 'money', width: 18 },
  { key: 'Stock Status', label: 'Stock Status', align: 'left', type: 'text', width: 16 },
];

const formatMoney = (value) => {
  return `Rs. ${Number(value || 0).toLocaleString('en-LK', {
    maximumFractionDigits: 2,
  })}`;
};

const formatNumber = (value) => Number(value || 0).toLocaleString('en-LK');

const formatPercent = (value) => {
  return `${Number(value || 0).toLocaleString('en-LK', {
    maximumFractionDigits: 2,
  })}%`;
};

const formatDateOnly = (date) => {
  if (!date) return '';
  return new Date(date).toLocaleDateString('en-LK');
};

const setColumnWidths = (worksheet, widths) => {
  worksheet['!cols'] = widths.map((width) => ({ wch: width }));
};

const formatCellValue = (value, type) => {
  if (type === 'money') return formatMoney(value);
  if (type === 'number') return formatNumber(value);
  if (type === 'percent') return formatPercent(value);
  return value || '';
};

const buildInventoryReportRows = (reportData) => {
  return (reportData?.rows || []).map((item) => ({
    Item: item.itemName || '',
    Brand: item.brandName || '',
    Generic: item.genericName || '',
    Batch: item.batchNo || '',
    Strength: item.strength || '',
    Supplier: item.supplier || '',
    'Expiry Date': formatDateOnly(item.expiryDate),
    'Expiry Status': item.expiryStatus || '',
    Quantity: item.quantity || 0,
    'Wholesale Price': item.wholesalePrice || 0,
    'Retail Price': item.retailPrice || 0,
    Discount: item.discount || 0,
    'Final Retail Price': item.finalRetailPrice || 0,
    'Stock Value': item.stockValue || 0,
    'Expected Sales Value': item.expectedSalesValue || 0,
    'Expected Profit': item.expectedProfit || 0,
    'Stock Status': item.stockStatus || '',
  }));
};

const downloadInventoryExcel = ({ filename, reportData }) => {
  if (!reportData) return;

  const inventoryRows = buildInventoryReportRows(reportData);

  const sheetRows = [
    INVENTORY_REPORT_COLUMNS.map((column) => column.label),
    ...inventoryRows.map((row) =>
      INVENTORY_REPORT_COLUMNS.map((column) => row[column.key])
    ),
  ];

  const worksheet = XLSX.utils.aoa_to_sheet(sheetRows);

  setColumnWidths(
    worksheet,
    INVENTORY_REPORT_COLUMNS.map((column) => column.width)
  );

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Inventory Report');

  XLSX.writeFile(workbook, filename);
};

const InventoryReportCard = () => {
  const [reportData, setReportData] = React.useState(null);
  const [previewOpen, setPreviewOpen] = React.useState(false);

  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState('');

  const loadReport = async () => {
    try {
      setLoading(true);
      setError('');

      const res = await fetchInventoryReport();
      setReportData(res.data);
    } catch (err) {
      console.error('Failed to fetch inventory report:', err);
      setError(err?.response?.data?.message || 'Failed to load inventory report.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadReport();
  }, []);

  const summary = reportData?.summary || {};
  const previewRows = buildInventoryReportRows(reportData);

  const handleDownload = () => {
    downloadInventoryExcel({
      filename: `inventory-report-${new Date().toISOString().slice(0, 10)}.xlsx`,
      reportData,
    });
  };

  return (
    <>
      <Card
        sx={{
          display: 'flex',
          flexDirection: 'column',
          borderRadius: '22px',
          p: { xs: 2, sm: 3 },
          border: '1px solid #e5e7eb',
          boxShadow: '0 10px 28px rgba(15, 23, 42, 0.07)',
          background: '#fff',
          height: '100%',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            gap: 2,
            mb: 3,
          }}
        >
          <Box
            sx={{
              width: 58,
              height: 58,
              borderRadius: '16px',
              background: '#eef2ff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <Inventory2OutlinedIcon
              sx={{
                color: '#4f46e5',
                fontSize: 30,
              }}
            />
          </Box>

          <Chip
            label={`${formatNumber(summary.totalItems)} ${
    summary.totalItems === 1 ? 'item' : 'items'
  }`}
            sx={{
              borderRadius: '999px',
              background: '#e0e7ff',
              color: '#3730a3',
              fontWeight: 800,
              fontSize: 14,
              height: 34,
              px: 1,
            }}
          />
        </Box>

        <Typography
          sx={{
            fontSize: 23,
            fontWeight: 900,
            color: '#111827',
            mb: 1,
          }}
        >
          Inventory Report
        </Typography>

        <Typography
          sx={{
            fontSize: 15.5,
            lineHeight: 1.65,
            color: '#6b7280',
            mb: 2.5,
          }}
        >
          Current stock, stock value, expected sales value and expected profit.
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(3, 1fr)',
            },
            gap: 1.2,
            mb: 2.5,
          }}
        >
          <MiniMetric label="Stock Purchase Value" value={formatMoney(summary.stockValue)} />
          <MiniMetric label="Expected Sales Value" value={formatMoney(summary.expectedSalesValue)} />
          <MiniMetric label="Expected Profit" value={formatMoney(summary.expectedProfit)} />
        </Box>

    <Box sx={{ mt: 'auto' }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: '150px 1fr',
            },
            gap: 1.4,
          }}
        >
          <Button
            variant="outlined"
            startIcon={<VisibilityOutlinedIcon />}
            onClick={() => setPreviewOpen(true)}
            disabled={loading || !reportData}
            sx={{
              height: 50,
              borderRadius: '12px',
              borderColor: '#e5e7eb',
              color: '#374151',
              fontWeight: 800,
              fontSize: 15,
              textTransform: 'none',
              background: '#fff',
              '&:hover': {
                borderColor: '#cbd5e1',
                background: '#f9fafb',
              },
            }}
          >
            Preview
          </Button>

          <Button
            variant="contained"
            startIcon={
              loading ? (
                <CircularProgress size={18} sx={{ color: '#fff' }} />
              ) : (
                <FileDownloadOutlinedIcon />
              )
            }
            onClick={handleDownload}
            disabled={loading || !reportData}
            sx={{
              height: 50,
              borderRadius: '12px',
              background: '#0f172a',
              color: '#fff',
              fontWeight: 900,
              fontSize: 15,
              textTransform: 'none',
              boxShadow: 'none',
              '&:hover': {
                background: '#111827',
                boxShadow: 'none',
              },
              '&:disabled': {
                background: '#94a3b8',
                color: '#fff',
              },
            }}
          >
            Download Excel
          </Button>
        </Box>
        </Box>
      </Card>

      <InventoryReportPreviewModal
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        summary={summary}
        rows={previewRows}
        generatedAt={reportData?.generatedAt}
      />
    </>
  );
};

const MiniMetric = ({ label, value }) => {
  return (
    <Box
      sx={{
        borderRadius: '14px',
        background: '#f8fafc',
        border: '1px solid #eef2f7',
        p: 1.4,
        minWidth: 0,
      }}
    >
      <Typography
        sx={{
          fontSize: 12,
          color: '#6b7280',
          fontWeight: 800,
          mb: 0.5,
        }}
      >
        {label}
      </Typography>

      <Typography
        sx={{
          fontSize: 15,
          color: '#111827',
          fontWeight: 900,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {value}
      </Typography>
    </Box>
  );
};

const InventoryReportPreviewModal = ({
  open,
  onClose,
  summary,
  rows,
  generatedAt,
}) => {
  const visibleRows = rows || [];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '22px',
          overflow: 'hidden',
        },
      }}
    >
      <DialogTitle
        sx={{
          px: 3,
          py: 2.5,
          borderBottom: '1px solid #e5e7eb',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 2,
        }}
      >
        <Box>
          <Typography
            sx={{
              fontSize: 24,
              fontWeight: 900,
              color: '#111827',
            }}
          >
            Inventory Report Preview
          </Typography>

          <Typography
            sx={{
              fontSize: 14,
              fontWeight: 600,
              color: '#6b7280',
              mt: 0.4,
            }}
          >
            Current stock snapshot
            {generatedAt ? ` · Generated ${formatDateOnly(generatedAt)}` : ''}
          </Typography>
        </Box>

        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent
        sx={{
          p: 0,
          backgroundColor: '#f8fafc',
        }}
      >
        <Box sx={{ p: 2.5, backgroundColor: '#ffffff' }}>
          <Typography
            sx={{
              fontSize: 16,
              fontWeight: 900,
              color: '#111827',
              mb: 1.5,
            }}
          >
            Summary
          </Typography>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, 1fr)',
                md: 'repeat(4, 1fr)',
              },
              gap: 1.4,
            }}
          >
            <PreviewMetric label="Total Items" value={formatNumber(summary.totalItems)} />
            <PreviewMetric label="Total Units" value={formatNumber(summary.totalUnits)} />
            <PreviewMetric label="Stock Purchase Value" value={formatMoney(summary.stockValue)} />
            <PreviewMetric label="Expected Sales Value" value={formatMoney(summary.expectedSalesValue)} />
            <PreviewMetric label="Expected Profit" value={formatMoney(summary.expectedProfit)} />
            <PreviewMetric label="Expired Stock Value" value={formatMoney(summary.expiredStockValue)} />
          </Box>
        </Box>

        <Divider />

        <Box
          sx={{
            p: 2,
            borderBottom: '1px solid #e5e7eb',
            backgroundColor: '#ffffff',
          }}
        >
          <Typography
            sx={{
              fontSize: 16,
              fontWeight: 900,
              color: '#111827',
            }}
          >
            Inventory Details
          </Typography>

          <Typography
            sx={{
              fontSize: 14,
              color: '#6b7280',
              fontWeight: 600,
              mt: 0.4,
            }}
          >
            These are the same columns included in the downloaded Excel sheet.
          </Typography>
        </Box>

        {visibleRows.length === 0 ? (
          <Box sx={{ p: 4 }}>
            <Typography color="text.secondary">No inventory data found.</Typography>
          </Box>
        ) : (
          <Box
            sx={{
              maxHeight: '60vh',
              overflow: 'auto',
              backgroundColor: '#ffffff',
            }}
          >
            <Box
              sx={{
                p: 2,
                backgroundColor: '#ffffff',
              }}
            >
              <Box
                sx={{
                  maxHeight: '55vh',
                  overflow: 'auto',
                  border: '1px solid #e5e7eb',
                  borderRadius: '14px',
                  backgroundColor: '#ffffff',
                }}
              >
                <Table
                  stickyHeader
                  size="small"
                  sx={{
                    minWidth: 1800,
                    '& th': {
                      backgroundColor: '#f3f4f6',
                      color: '#374151',
                      fontWeight: 900,
                      whiteSpace: 'nowrap',
                      borderBottom: '1px solid #d1d5db',
                    },
                    '& td': {
                      whiteSpace: 'nowrap',
                      borderBottom: '1px solid #eef2f7',
                      fontSize: 13,
                    },
                  }}
                >
                  <TableHead>
                    <TableRow>
                      {INVENTORY_REPORT_COLUMNS.map((column) => (
                        <TableCell key={column.key} align={column.align}>
                          {column.label}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {visibleRows.map((row, index) => (
                      <TableRow key={`${row.Item}-${row.Batch}-${index}`}>
                        {INVENTORY_REPORT_COLUMNS.map((column) => (
                          <TableCell key={column.key} align={column.align}>
                            {formatCellValue(row[column.key], column.type)}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            </Box>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

const PreviewMetric = ({ label, value }) => {
  return (
    <Box
      sx={{
        backgroundColor: '#f8fafc',
        border: '1px solid #e5e7eb',
        borderRadius: '14px',
        p: 1.5,
      }}
    >
      <Typography
        sx={{
          color: '#6b7280',
          fontSize: 12,
          fontWeight: 800,
          mb: 0.5,
        }}
      >
        {label}
      </Typography>

      <Typography
        sx={{
          color: '#111827',
          fontSize: 17,
          fontWeight: 900,
        }}
      >
        {value}
      </Typography>
    </Box>
  );
};

export default InventoryReportCard;